import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ArrowRightOutlined } from '@ant-design/icons';
import rawSnapshot from './orgStatsSnapshot.json';
import { trackEvent } from '../../../utils/analytics';
import { filterOrgData } from './orgDisplayPolicy';
import { useAuth } from '../../../contexts/AuthContext';
import { saveIntendedUrl } from '../../../utils/persistentNavigation';
import { BENCHMARK_LABEL } from '../../../content/benchmarkClaim';
import './AIOrganizationLive.css';

/**
 * OrgLiveDashboard — the live layer of /ai-organization. v2 (Nitish feedback round).
 *
 * DATA: renders from a build-time snapshot (orgStatsSnapshot.json, baked so the
 * prerendered HTML carries real content), then hydrates from the daily-refreshed
 * /assets/org-stats/org_stats.json at runtime. Generated by
 * agent_docs/rhea_ai_org_live/generate_org_stats.py — INTERNAL (@cellcog.ai-owned)
 * employees ONLY (privacy hard requirement); counts/aggregates only. Days are ET.
 *
 * v2: org chart is a GRAPH with connector lines (deck-style: founder -> leads ->
 * Reese's reps); card click opens a centered MODAL; avatar initials use dark ink.
 * Charts are hand-drawn inline SVG (prerender-safe, zero bundle weight).
 *
 * v3 (bugfix round): connectors are proper dashed lines aligned to column
 * centers; modal renders via portal to <body> (ancestor reveal transforms were
 * re-anchoring position:fixed and throwing it off-screen); all credit figures
 * display as dollars (credits / 100).
 *
 * v4 (landing reshuffle, Jul 23): OrgLiveDashboard renders GRAPH FIRST ->
 * totals -> time controls + activity chart (the org chart is the page's
 * landing visual; the range controls sit with the chart they most visibly
 * drive, and still re-window the graph card minis). BuildDashboard unchanged.
 */

// Display policy applied ONCE at module load — every consumer in this file
// (org-page dashboard, homepage hero + story) renders the same visible set.
const snapshot = filterOrgData(rawSnapshot);

const METRICS = [
  { key: 'c', label: 'Spend' },
  { key: 't', label: 'Tasks closed' },
  { key: 'e', label: 'Emails sent' },
  { key: 's', label: 'Shifts' },
];

const fmt = (n) => (n >= 10000 ? `${(n / 1000).toFixed(n >= 100000 ? 0 : 1)}k` : n.toLocaleString());
// Credits -> display dollars (100 credits = $1).
const fmtUsd = (credits) => {
  const d = credits / 100;
  if (d >= 10000) return `$${(d / 1000).toFixed(d >= 100000 ? 0 : 1)}k`;
  return `$${d >= 100 ? Math.round(d).toLocaleString() : d.toFixed(2)}`;
};
const prettyDate = (iso) => {
  if (!iso) return ''; // range indices can briefly outrun a shorter fetched day list
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

function buildDays(data) {
  let min = null;
  let max = null;
  data.employees.forEach((e) => {
    Object.keys(e.daily).forEach((d) => {
      if (!min || d < min) min = d;
      if (!max || d > max) max = d;
    });
  });
  if (!min) return [];
  const days = [];
  const cur = new Date(`${min}T00:00:00Z`);
  const end = new Date(`${max}T00:00:00Z`);
  while (cur <= end) {
    days.push(cur.toISOString().slice(0, 10));
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return days;
}

const dayVal = (day, m) => (m === 'e' ? (day.ei || 0) + (day.ee || 0) : day[m] || 0);

function windowTotals(emp, days, a, b) {
  const t = { s: 0, c: 0, t: 0, e: 0, d: 0 };
  for (let i = a; i <= b; i += 1) {
    const day = emp.daily[days[i]];
    if (day) {
      t.s += day.s || 0; t.c += day.c || 0; t.t += day.t || 0;
      t.e += (day.ei || 0) + (day.ee || 0); t.d += day.d || 0;
    }
  }
  return t;
}

function Sparkline({ series, className }) {
  const max = Math.max(...series, 1);
  const w = 100;
  const h = 24;
  const step = series.length > 1 ? w / (series.length - 1) : w;
  const pts = series.map((v, i) => `${(i * step).toFixed(1)},${(h - 2 - (v / max) * (h - 5)).toFixed(1)}`).join(' ');
  return (
    <svg className={className} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
      <polyline points={pts} fill="none" stroke="currentColor" strokeWidth="1.6" opacity="0.75" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function ActivityChart({ days, series, metricLabel, fmtValue = fmt }) {
  const w = 820;
  const h = 230;
  const padL = 44;
  const padB = 26;
  const padT = 12;
  const max = Math.max(...series, 1);
  const innerW = w - padL - 10;
  const innerH = h - padT - padB;
  const x = (i) => padL + (series.length > 1 ? (i / (series.length - 1)) * innerW : 0);
  const y = (v) => padT + innerH - (v / max) * innerH;
  const line = series.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const area = `${line} L${x(series.length - 1).toFixed(1)},${(padT + innerH).toFixed(1)} L${padL},${(padT + innerH).toFixed(1)} Z`;
  const gridVals = [0.25, 0.5, 0.75, 1].map((f) => Math.round(max * f));
  const tickEvery = Math.max(1, Math.floor(days.length / 6));
  return (
    <svg className="aio-chart-svg" viewBox={`0 0 ${w} ${h}`} role="img" aria-label={`${metricLabel} per day`}>
      {gridVals.map((v) => (
        <g key={v}>
          <line x1={padL} x2={w - 10} y1={y(v)} y2={y(v)} stroke="currentColor" opacity="0.09" />
          <text x={padL - 7} y={y(v) + 3.5} textAnchor="end" fontSize="10.5" fill="currentColor" opacity="0.5">{fmtValue(v)}</text>
        </g>
      ))}
      <path d={area} fill="currentColor" opacity="0.07" />
      <path d={line} fill="none" stroke="currentColor" strokeWidth="2" opacity="0.85" />
      {days.map((d, i) => (i % tickEvery === 0 ? (
        <text key={d} x={x(i)} y={h - 8} textAnchor="middle" fontSize="10.5" fill="currentColor" opacity="0.5">{prettyDate(d)}</text>
      ) : null))}
    </svg>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <span className="aio-stat">
      <span className="aio-stat__label">{label}</span>
      <span className="aio-stat__value">{value}</span>
      {sub ? <span className="aio-stat__sub">{sub}</span> : <span className="aio-stat__sub">&nbsp;</span>}
    </span>
  );
}

/* ---------- live status (v5, Jul 24): on-shift / starting-soon / next-shift /
   on-demand, computed CLIENT-SIDE from the generator's precomputed occurrence
   list (emp.live.upcoming) + the employee's real median shift length. A baked
   boolean would go stale (the JSON refreshes daily); this ticks every 30s.
   Honesty: "On shift" = inside the SCHEDULED window (schedule truth), not a
   claim about live execution. ---------- */

const SOON_MS = 60 * 60 * 1000; // "starting soon" horizon: 1 hour

function liveStatus(live, nowMs) {
  if (!live) return null;
  if (live.mode === 'on_demand') return { kind: 'on_demand', label: 'On-demand' };
  const upcoming = live.upcoming || [];
  if (!upcoming.length) return { kind: 'idle', label: 'Unscheduled' };
  const windowMs = (live.typical_shift_minutes || 360) * 60 * 1000;
  let next = null;
  for (const iso of upcoming) {
    const t = Date.parse(iso);
    if (nowMs >= t && nowMs < t + windowMs) return { kind: 'on', label: 'Working now' };
    if (t > nowMs) { next = t; break; }
  }
  if (next === null) return { kind: 'idle', label: 'Unscheduled' };
  if (next - nowMs <= SOON_MS) {
    const mins = Math.max(1, Math.round((next - nowMs) / 60000));
    return { kind: 'soon', label: `Starts in ${mins}m` };
  }
  const d = new Date(next);
  const et = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' });
  const today = new Date(nowMs).toLocaleDateString('en-US', { timeZone: 'America/New_York' });
  const nextDay = d.toLocaleDateString('en-US', { timeZone: 'America/New_York' });
  return { kind: 'next', label: `Next ${today === nextDay ? '' : d.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'America/New_York' }) + ' '}${et} ET` };
}

// Shared 30s clock so every card re-derives its status together.
function useNowMs() {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);
  return now;
}

// REAL execution signal (v6): /api/cellcog/org-live exposes each internal
// employee's actual `operating` flag (public, server-cached 30s, 3 bulk
// queries). Polled every 60s; returns {name: {operating, mode}} or null while
// unavailable — the schedule-derived status is the graceful fallback, so the
// chart degrades to v5 behavior (never blanks) if the API is unreachable.
function useOrgLive() {
  const [liveByName, setLiveByName] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      fetch('/api/cellcog/org-live')
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (cancelled || !d || !Array.isArray(d.employees) || !d.employees.length) return;
          const map = {};
          d.employees.forEach((e) => { map[e.name] = e; });
          setLiveByName(map);
        })
        .catch(() => {});
    };
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  return liveByName;
}

// Overlay the real signal on the schedule-derived status. `operating: true`
// always wins (catches on-demand sessions + early scheduled starts); a real
// `false` demotes a schedule-guessed "on" to the next-shift display (catches
// shifts that ended early). No real data -> schedule status unchanged.
function mergedStatus(live, realEmp, nowMs) {
  const sched = liveStatus(live, nowMs);
  if (!realEmp) return sched;
  if (realEmp.operating) return { kind: 'on', label: 'Operating now' };
  if (sched && sched.kind === 'on') {
    const nextIso = (live && (live.upcoming || []).find((iso) => Date.parse(iso) > nowMs)) || null;
    if (!nextIso) return live && live.mode === 'on_demand' ? { kind: 'on_demand', label: 'On-demand' } : { kind: 'idle', label: 'Off' };
    const withNext = liveStatus({ ...live, upcoming: [nextIso] }, nowMs);
    return withNext && withNext.kind !== 'on' ? withNext : { kind: 'idle', label: 'Off' };
  }
  return sched;
}

function StatusLine({ live, nowMs }) {
  const st = liveStatus(live, nowMs);
  if (!st) return null;
  return (
    <span className={`aio-status aio-status--${st.kind}`}>
      <span className="aio-status__dot" aria-hidden="true" />
      {st.label}
    </span>
  );
}

// NOTE: no sparkline on graph cards — an unlabeled line invited "what is this?"
// confusion (WhatsApp review, Jul 24). The per-employee spend/day sparkline
// lives in the ProfileModal, where it has an explanatory caption.
// LIVE STATUS DESIGN (declutter round, Jul 24): presence dot ON the avatar
// (Slack-style — green pulse = on shift, amber = starting soon, quiet gray =
// off, teal = on-demand) + ONE muted text fragment appended to the meta line.
// No extra rows, no second color-coded element — less is more.
function NodeCard({ emp, wt, onOpen, human, nowMs, showCta = false }) {
  // ROOT node = the company, not a person (Nitish, Sep 26 2026: no human headcount
  // anywhere on the chart; the leads hang under CellCog). Slim (no description line): the whole org
  // must fit in the landing view, so every vertical pixel counts.
  // plainFounder (homepage, round-6 mock ruling Aug 17): "Founder" over the
  // name, and no headcount line — the single-human count dates and can read
  // as continuity risk. /ai-organization keeps the full wording.
  if (human) {
    return (
      <div className="aio-card is-human aio-node">
        <div className="aio-card__top aio-card__top--solo">
          <span className="aio-avatar" aria-hidden="true">C</span>
          <span className="aio-card__who">
            <span className="aio-card__name">CellCog</span>
            <span className="aio-card__role">Company</span>
          </span>
        </div>
      </div>
    );
  }
  const st = mergedStatus(emp.live, emp.realLive, nowMs);
  return (
    <button type="button" className="aio-card aio-node" onClick={onOpen}>
      <div className="aio-card__top">
        <span className="aio-avatar-wrap">
          <span className="aio-avatar" aria-hidden="true">{emp.name[0]}</span>
          {st && <span className={`aio-presence aio-presence--${st.kind}`} aria-hidden="true" />}
        </span>
        <span className="aio-card__who">
          <span className="aio-card__name">
            {emp.name}
            {emp.is_manager && <span className="aio-pill is-manager">Manager</span>}
          </span>
          <span className="aio-card__role">{emp.title}</span>
        </span>
      </div>
      {/* Oct 6 2026 (Nitish): compact card. Status = a small "Live" badge on the
          card's top-right edge (only while working; the avatar dot carries the rest),
          no "On-demand", no shifts or cost (stale since cheaper tiers; "shift" is
          retired copy). Hire date and tasks share ONE line. */}
      {st && st.kind === 'on' && <span className="aio-card__live"><span aria-hidden="true" />Live</span>}
      <div className="aio-card__meta aio-card__meta--one">
        Hired {prettyDate(emp.hired)} · <strong>{wt.t}</strong> tasks
      </div>
      {showCta && (
        <span className="aio-card__cta"><span>View live activity</span><span aria-hidden="true">↗</span></span>
      )}
    </button>
  );
}

/* ---------- mobile roster row (Aug 3 mobile pass) ----------
   One tight tappable row per employee — the whole org lands in ~1.5 phone
   screens with the reporting structure visible (group labels + team indent).
   Taps open the same profile as the desktop cards. */
function RosterRow({ emp, wt, onOpen, nowMs, team = false }) {
  const st = mergedStatus(emp.live, emp.realLive, nowMs);
  return (
    <button type="button" className={`aio-rrow${team ? ' aio-rrow--team' : ''}`} onClick={onOpen}>
      <span className="aio-avatar-wrap">
        <span className="aio-avatar" aria-hidden="true">{emp.name[0]}</span>
        {st && <span className={`aio-presence aio-presence--${st.kind}`} aria-hidden="true" />}
      </span>
      <span className="aio-rrow__main">
        <span className="aio-rrow__name">
          {emp.name}
          {emp.is_manager && <span className="aio-pill is-manager">Manager</span>}
        </span>
        <span className="aio-rrow__role">{emp.title}</span>
      </span>
      <span className="aio-rrow__stats">
        <span className="aio-rrow__s1">{wt.t} tasks</span>
      </span>
      <span className="aio-rrow__chev" aria-hidden="true">›</span>
    </button>
  );
}

/* ---------- the org graph (shared: /ai-organization + the homepage hero) ---------- */

/* Build the org TREE the data describes: leads = direct reports of the
   founder; below them, one LEVEL per depth of the reporting tree — each
   manager's reports form a GROUP that renders on the next level and slides
   under that manager's card at render time (v8, Aug 12 round: pure
   level-by-level layout, no team boxes). Fully data-driven — a new hire (or
   a whole new team) appears the moment the generator's reporting map carries
   their line; nothing here names employees. Team labels (used by the mobile
   roster groups) come from the generator's team_names map, falling back to
   "{Name}'s team". */
function buildOrgTree(perEmp, teamNames) {
  const tn = teamNames || {};
  const teamLabel = (name) => tn[name] || `${name}’s team`;
  const childrenOf = new Map();
  perEmp.forEach((item) => {
    const m = item.emp.reports_to;
    if (!childrenOf.has(m)) childrenOf.set(m, []);
    childrenOf.get(m).push(item);
  });
  // Lead order (Nitish, Oct 6 2026): a manager sits where its team fits, so every
  // report hangs straight under its manager. Rule, never a name list: the biggest
  // team goes rightmost, smaller teams from second-left, people with no reports
  // fill the middle (data order). Next hire keeps the shape.
  const subtree = (name) => (childrenOf.get(name) || []).reduce((n, { emp }) => n + 1 + subtree(emp.name), 0);
  const rawLeads = childrenOf.get('Nitish') || [];
  const mgrs = rawLeads.filter(({ emp }) => childrenOf.has(emp.name))
    .sort((x, y) => subtree(y.emp.name) - subtree(x.emp.name));
  const plain = rawLeads.filter(({ emp }) => !childrenOf.has(emp.name));
  let leads = mgrs.length && plain.length
    ? [plain[0], ...mgrs.slice(1).reverse(), ...plain.slice(1), mgrs[0]]
    : rawLeads;

  // LEVELS below the leads row, breadth-first: level k holds one GROUP per
  // level-(k-1) manager with reports; the group's members render side by
  // side and the whole group slides under its manager at render time. Depth
  // is unbounded — a 4th level appears the moment the data carries one.
  const levels = [];
  let frontier = leads;
  while (frontier.length) {
    const lvl = frontier
      .filter(({ emp }) => childrenOf.has(emp.name))
      .map(({ emp }) => ({ manager: emp.name, members: childrenOf.get(emp.name) }));
    if (!lvl.length) break;
    levels.push(lvl);
    frontier = lvl.reduce((acc, g) => acc.concat(g.members), []);
  }

  // Fail-open: an employee whose manager isn't rendered (e.g. hidden by the
  // display policy in orgDisplayPolicy.js) surfaces on the leads row instead
  // of silently vanishing from the chart.
  const placed = new Set(leads.map(({ emp }) => emp.name));
  levels.forEach((lvl) => lvl.forEach((g) => g.members.forEach(({ emp }) => placed.add(emp.name))));
  const orphans = perEmp.filter(({ emp }) => !placed.has(emp.name));
  if (orphans.length) leads = leads.concat(orphans);

  // Mobile roster groups: one labeled group per manager, tree (DFS) order so
  // a nested team reads directly under its manager's group.
  const groups = [];
  const addGroup = (mgr) => {
    const kids = childrenOf.get(mgr);
    if (!kids) return;
    groups.push({ manager: mgr, label: `${teamLabel(mgr)} · led by ${mgr}`, members: kids });
    kids.forEach(({ emp }) => addGroup(emp.name));
  };
  leads.forEach(({ emp }) => addGroup(emp.name));

  return { leads, levels, groups };
}

function OrgGraph({ leads, levels, groups, onCard, caption, className = 'aio-graph--lead', cardCta = false, plainFounder = false }) {
  const nowMs = useNowMs(); // one clock for the whole graph — statuses tick together
  const orgLive = useOrgLive(); // real operating flags (null until first fetch)
  const graphRef = React.useRef(null);
  const withReal = ({ emp, wt }) => ({ emp: orgLive ? { ...emp, realLive: orgLive[emp.name] } : emp, wt });

  /* Layout + wires (v8, Aug 12 design round with Nitish): pure level-by-level
     layout — founder, leads, then one row per reporting depth. Every group of
     reports slides horizontally so it centers under its manager's card, and
     every connector is drawn from real card positions in one SVG overlay
     using ONLY vertical and horizontal segments (his rule: never tilted,
     never elbows). Positions are measured after paint and re-measured on
     resize / font load / data changes. Without JS the chart still reads —
     centered card rows, no lines (fail-open, same doctrine as useReveal). */
  React.useLayoutEffect(() => {
    const root = graphRef.current;
    if (!root) return undefined;
    const draw = () => {
      const svg = root.querySelector('.aio-graph__wires');
      const sR = root.getBoundingClientRect();
      if (!svg || sR.width < 20) return; // hidden at the mobile breakpoint
      const rect = (el) => {
        const r = el.getBoundingClientRect();
        return { x: r.x - sR.x, cx: r.x - sR.x + r.width / 2, top: r.y - sR.y, bot: r.y - sR.y + r.height, w: r.width };
      };
      const cardIn = (scope, name) => scope.querySelector(`[data-name="${name}"] .aio-node`);

      // 0) every level uses the leads row's card width (Oct 6 2026): a wider lone
      // report under the rightmost manager hit the stage edge and bent its line.
      const firstLead = root.querySelector('[data-leads-row] [data-name]');
      if (firstLead) root.style.setProperty('--aio-cell-w', `${Math.round(firstLead.getBoundingClientRect().width)}px`);

      // 1) slide each GROUP so its members center under its manager's card;
      // clamp against neighbors on the same level and the stage edges
      const grpEls = Array.from(root.querySelectorAll('[data-grp]'));
      grpEls.forEach((g) => { g.style.transform = 'none'; });
      Array.from(root.querySelectorAll('.aio-graph__lvl')).forEach((lvlEl) => {
        let prevRight = null;
        Array.from(lvlEl.querySelectorAll('[data-grp]')).forEach((g) => {
          const mgrCard = cardIn(root, g.dataset.grp);
          const kids = Array.from(g.children).filter((c) => c.hasAttribute('data-name')).map((c) => rect(c));
          if (!mgrCard || !kids.length) return;
          const mid = (kids[0].cx + kids[kids.length - 1].cx) / 2;
          let shift = rect(mgrCard).cx - mid;
          const gx = rect(g);
          if (prevRight !== null && gx.x + shift < prevRight + 16) shift = prevRight + 16 - gx.x;
          if (gx.x + shift + gx.w > sR.width) shift = sR.width - (gx.x + gx.w);
          if (gx.x + shift < 0) shift = -gx.x;
          g.style.transform = Math.abs(shift) > 0.5 ? `translateX(${shift.toFixed(1)}px)` : 'none';
          prevRight = gx.x + shift + gx.w;
        });
      });

      // 1b) centre the ROOT card over the leads row (Nitish, Sep 26 2026: with a
      // fifth lead the rows centred on different widths and the root read
      // off-centre). Measured like the group slide above, clamped to the stage.
      const rootCard = root.querySelector('[data-founder] .aio-card');
      const leadRowEl = root.querySelector('[data-leads-row]');
      if (rootCard && leadRowEl) {
        rootCard.style.transform = 'none';
        const lc = Array.from(leadRowEl.children)
          .filter((c) => c.hasAttribute('data-name'))
          .map((c) => rect(c.querySelector('.aio-node') || c));
        if (lc.length) {
          const mid = (lc[0].cx + lc[lc.length - 1].cx) / 2;
          const rc = rect(rootCard);
          let s = mid - rc.cx;
          if (rc.x + s < 0) s = -rc.x;
          if (rc.x + s + rc.w > sR.width) s = sR.width - (rc.x + rc.w);
          rootCard.style.transform = Math.abs(s) > 0.5 ? `translateX(${s.toFixed(1)}px)` : 'none';
        }
      }

      // 2) wires, from re-measured (post-slide) positions. ONE visual
      // language everywhere (his rule: only vertical and horizontal
      // segments): the manager drops VERTICALLY onto a bus 14px above their
      // group, the bus spans the drop point and every member, and each
      // member hangs off the bus. A single report aligned under its manager
      // makes the bus zero-length — the connector reads as one straight line.
      const P = [];
      const wireGroup = (fromR, cells) => {
        if (!cells.length) return;
        const busY = Math.min(...cells.map((c) => c.top)) - 14;
        P.push(`M ${fromR.cx} ${fromR.bot} V ${busY}`);
        const x0 = Math.min(cells[0].cx, fromR.cx);
        const x1 = Math.max(cells[cells.length - 1].cx, fromR.cx);
        if (x1 - x0 > 1) P.push(`M ${x0} ${busY} H ${x1}`);
        cells.forEach((c) => P.push(`M ${c.cx} ${busY} V ${c.top}`));
      };
      const cellsOf = (rowEl) => Array.from(rowEl.children)
        .filter((c) => c.hasAttribute('data-name'))
        .map((c) => rect(c.querySelector('.aio-node') || c));
      const founderEl = root.querySelector('[data-founder] .aio-card');
      const leadsRow = root.querySelector('[data-leads-row]');
      if (founderEl && leadsRow) wireGroup(rect(founderEl), cellsOf(leadsRow));
      grpEls.forEach((g) => {
        const mgrCard = cardIn(root, g.dataset.grp);
        if (mgrCard) wireGroup(rect(mgrCard), cellsOf(g));
      });
      svg.setAttribute('viewBox', `0 0 ${Math.round(sR.width)} ${Math.round(sR.height)}`);
      svg.innerHTML = P.map((d) => `<path d="${d}"/>`).join('');
    };
    let raf = 0;
    const schedule = () => { window.cancelAnimationFrame(raf); raf = window.requestAnimationFrame(draw); };
    schedule();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(schedule).catch(() => {});
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
    if (ro) ro.observe(root);
    window.addEventListener('resize', schedule);
    return () => {
      window.cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
      window.removeEventListener('resize', schedule);
    };
  }, [leads, levels, orgLive]);

  return (
    <div className={`aio-graph ${className}`}>
      {/* DESKTOP/TABLET: founder -> leads row -> level rows, with real
          reporting lines drawn in the SVG overlay. Both layouts below are
          always rendered (so the prerendered HTML carries both); CSS shows
          exactly one per breakpoint — no JS/matchMedia at first paint (same
          fail-open doctrine as useReveal). */}
      <div className="aio-orgview--graph" ref={graphRef}>
        <svg className="aio-graph__wires" aria-hidden="true" />
        <div className="aio-graph__row aio-graph__row--founder" data-founder="true">
          <NodeCard human plainFounder={plainFounder} />
        </div>
        <div
          className="aio-graph__row aio-graph__row--leads"
          data-leads-row="true"
          style={{ gridTemplateColumns: `repeat(${leads.length}, minmax(0, 230px))`, justifyContent: 'center', columnGap: 30 }}
        >
          {leads.map(withReal).map(({ emp, wt }) => (
            <div className="aio-graph__cell" data-name={emp.name} key={emp.name}>
              <NodeCard emp={emp} wt={wt} nowMs={nowMs} showCta={cardCta} onOpen={() => onCard(emp)} />
            </div>
          ))}
        </div>
        {levels.map((lvl, i) => (
          <div className="aio-graph__lvl" key={i}>
            {lvl.map((g) => (
              <div
                className="aio-graph__grp"
                data-grp={g.manager}
                key={g.manager}
                style={{ gridTemplateColumns: `repeat(${g.members.length}, minmax(0, var(--aio-cell-w, 230px)))` }}
              >
                {g.members.map(withReal).map(({ emp, wt }) => (
                  <div className="aio-graph__cell" data-name={emp.name} key={emp.name}>
                    <NodeCard emp={emp} wt={wt} nowMs={nowMs} showCta={cardCta} onOpen={() => onCard(emp)} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>
      {/* MOBILE (<=640px): compact roster — chosen over swipe cards because the
          page's proof is the org's SCALE + STRUCTURE, which must land without
          any interaction (93% mobile bounce taught us that). Groups derive
          from the same reporting tree as the desktop graph, one labeled group
          per manager. */}
      <div className="aio-orgview--roster">
        <div className="aio-roster__founder"><NodeCard human plainFounder={plainFounder} /></div>
        <span className="aio-roster__stem" aria-hidden="true" />
        <div className="aio-roster__grp">Leadership</div>
        {leads.map(withReal).map(({ emp, wt }) => (
          <RosterRow key={emp.name} emp={emp} wt={wt} nowMs={nowMs} onOpen={() => onCard(emp, 'roster')} />
        ))}
        {groups.map(({ manager, label, members }) => (
          <React.Fragment key={manager}>
            <div className="aio-roster__grp">{label}</div>
            {members.map(withReal).map(({ emp, wt }) => (
              <RosterRow key={emp.name} team emp={emp} wt={wt} nowMs={nowMs} onOpen={() => onCard(emp, 'roster')} />
            ))}
          </React.Fragment>
        ))}
      </div>
      {caption && <p className="aio-graph__caption">{caption}</p>}
    </div>
  );
}

/**
 * OrgHeroStory — the homepage hero's narrative block UNDER the org chart:
 * the two ownership bands with headline totals, the all-time super-agent
 * spend chart, and the closing "who writes the software" line. Snapshot-only
 * (prerender-safe, zero requests).
 *
 * WHY (Nitish, Jul 24): the org chart alone leaves a hole — there are no
 * engineers on it, so a visitor wonders who builds the software. This block
 * closes the loop: AI employees scale the company; super agents build it;
 * together that's the company's entire bill.
 */
export function OrgHeroStory() {
  // Metric toggles on BOTH charts (Nitish, Jul 24): employees get the org-page
  // metric set (spend/tasks/emails/shifts); super agents get theirs
  // (spend/chats/machine actions) — same interaction language as /ai-organization.
  const [empMetric, setEmpMetric] = useState('c');
  const [saMetric, setSaMetric] = useState('c');

  const emp = snapshot.employees.reduce(
    (a, e) => ({ s: a.s + e.totals.shifts, c: a.c + e.totals.credits, t: a.t + e.totals.tasks_closed }),
    { s: 0, c: 0, t: 0 },
  );
  const sa = snapshot.super_agents;
  if (!sa || !sa.daily) return null;

  // Employees: whole-org daily series across all snapshot days.
  const empDays = buildDays(snapshot);
  const empSeries = empDays.map((d) => {
    let v = 0;
    snapshot.employees.forEach((e) => { const day = e.daily[d]; if (day) v += dayVal(day, empMetric); });
    return v;
  });
  const empMetricLabel = METRICS.find((m) => m.key === empMetric).label;

  // Super agents: daily series; machine actions clamp to the audit window
  // (90-day retention — same per-metric honesty rule as BuildDashboard).
  let saDays = Object.keys(sa.daily).sort();
  if (saMetric === 'hc' && sa.hc_window_start) saDays = saDays.filter((d) => d >= sa.hc_window_start);
  const saSeries = saDays.map((d) => (sa.daily[d] ? sa.daily[d][saMetric] || 0 : 0));
  const saMetricLabel = BUILD_METRICS.find((m) => m.key === saMetric).label;

  return (
    <div className="aio-story">
      <div className="aio-band">
        <span className="aio-band__tag">AI Employees</span>
        <span className="aio-band__title">How we scale the company</span>
      </div>
      <div className="aio-totals aio-totals--hero">
        <StatCard label="Shifts worked" value={emp.s} />
        <StatCard label="Spend" value={fmtUsd(emp.c)} sub={`≈ ${emp.s ? fmtUsd(emp.c / emp.s) : '$0'} per shift`} />
        <StatCard label="Tasks closed" value={emp.t} />
      </div>
      {empDays.length > 1 && (
        <div className="aio-chart-card">
          <div className="aio-chart-head">
            <span className="aio-chart-title">{empMetricLabel} per day, whole org</span>
            <span className="aio-metric-toggle">
              {METRICS.map((m) => (
                <button key={m.key} type="button" className={`aio-metric-btn ${empMetric === m.key ? 'is-active' : ''}`} onClick={() => setEmpMetric(m.key)}>{m.label}</button>
              ))}
            </span>
          </div>
          <ActivityChart days={empDays} series={empSeries} metricLabel={empMetricLabel} fmtValue={empMetric === 'c' ? fmtUsd : fmt} />
        </div>
      )}

      <div className="aio-band">
        <span className="aio-band__tag">Super Agents</span>
        <span className="aio-band__title">How we build the company</span>
      </div>
      <div className="aio-totals aio-totals--hero">
        <StatCard label="Chats run" value={fmt(sa.totals.chats)} sub="since Jan 1, 2026" />
        <StatCard label="Spend" value={fmtUsd(sa.totals.credits)} sub={`≈ ${sa.totals.chats ? fmtUsd(sa.totals.credits / sa.totals.chats) : '$0'} per chat`} />
        <StatCard label="Artifacts generated" value={fmt(sa.totals.artifacts)} sub="images · video · audio · documents" />
      </div>
      <div className="aio-chart-card">
        <div className="aio-chart-head">
          <span className="aio-chart-title">{saMetricLabel} per day, super agents</span>
          <span className="aio-metric-toggle">
            {BUILD_METRICS.map((m) => (
              <button key={m.key} type="button" className={`aio-metric-btn ${saMetric === m.key ? 'is-active' : ''}`} onClick={() => setSaMetric(m.key)}>{m.label}</button>
            ))}
          </span>
        </div>
        {saSeries.length > 1 && (
          <ActivityChart days={saDays} series={saSeries} metricLabel={saMetricLabel} fmtValue={saMetric === 'c' ? fmtUsd : fmt} />
        )}
        {saMetric === 'hc' && sa.hc_window_start && (
          <p className="aio-chart-note">Machine actions are recorded since {prettyDate(sa.hc_window_start)} — the per-command audit log keeps 90 days.</p>
        )}
      </div>

      <p className="aio-story__closer">
        Notice what the org chart is missing: engineers. The software behind CellCog, including the
        deep-reasoning engine ranked {BENCHMARK_LABEL}, is written by the super agents.
        {' '}{fmtUsd(emp.c)} to scale the company, {fmtUsd(sa.totals.credits)} to build it. That is the
        entire bill.
      </p>
    </div>
  );
}

/**
 * OrgHeroGraph — the homepage hero-stage variant of the org chart. All-time
 * totals straight from the build-time snapshot: no fetch, so the prerendered
 * homepage carries the full graph. Card clicks open the same ProfileModal as
 * /ai-organization right there (navigating away from the hero was a bad
 * experience — Nitish, Jul 24); the optional onCard callback is for
 * analytics only.
 */
export function OrgHeroGraph({ onCard, dark = true, plainFounder = false }) {
  const [openEmp, setOpenEmp] = useState(null);
  const days = buildDays(snapshot);
  if (!days.length) return null;
  const perEmp = snapshot.employees.map((e) => {
    const spark = days.map((d) => (e.daily[d] ? dayVal(e.daily[d], 'c') : 0));
    return { emp: e, wt: windowTotals(e, days, 0, days.length - 1), spark };
  });
  const { leads, levels, groups } = buildOrgTree(perEmp, snapshot.team_names);
  const opened = openEmp ? perEmp.find(({ emp }) => emp.name === openEmp) : null;
  return (
    <>
      <OrgGraph
        leads={leads}
        levels={levels}
        groups={groups}
        plainFounder={plainFounder}
        onCard={(emp, layout) => { if (onCard) onCard(emp, layout || 'graph'); setOpenEmp(emp.name); }}
        className="aio-graph--lead aio-graph--hero"
        caption="AI employees managing AI employees, with real hire dates and real work. Click any card for the full profile."
      />
      {opened && <ProfileModal emp={opened.emp} wt={opened.wt} spark={opened.spark} dark={dark} onClose={() => setOpenEmp(null)} />}
    </>
  );
}

function ProfileModal({ emp, wt, spark, dark, onClose }) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  // Peak-interest exit (Aug 3 pass): someone who opened a profile cared enough
  // to click — the modal used to end on a "coming soon" dead end.
  const handleModalHire = () => {
    trackEvent('cta_click', { cta_name: 'hire_ai_employee', location: 'profile_modal', employee: emp.name });
    if (!currentUser) { saveIntendedUrl('/ai_employee'); navigate('/signup'); return; }
    navigate('/ai_employee');
  };
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose]);
  // Swipe-down-to-dismiss (Aug 3 mobile UX round): drag starts on the pinned
  // HEAD only (the sheet body keeps native scrolling; the head has
  // touch-action:none in CSS). >110px of downward travel closes; anything
  // less springs back. Desktop fires no touch events, so this is inert there.
  const sheetRef = React.useRef(null);
  const dragRef = React.useRef({ y0: 0, dy: 0, on: false });
  const headTouchStart = (e) => {
    // Phones only: an iPad fires touch events too, but at >640px the profile
    // is the CENTERED modal — dragging that around would be broken, not native.
    if (!window.matchMedia('(max-width: 640px)').matches) return;
    dragRef.current = { y0: e.touches[0].clientY, dy: 0, on: true };
    if (sheetRef.current) sheetRef.current.style.transition = 'none';
  };
  const headTouchMove = (e) => {
    const d = dragRef.current;
    if (!d.on) return;
    d.dy = Math.max(0, e.touches[0].clientY - d.y0);
    if (sheetRef.current) sheetRef.current.style.transform = `translateY(${d.dy}px)`;
  };
  const headTouchEnd = () => {
    const d = dragRef.current;
    if (!d.on) return;
    d.on = false;
    const el = sheetRef.current;
    if (!el) return;
    el.style.transition = 'transform .2s ease';
    if (d.dy > 110) {
      el.style.transform = 'translateY(105%)';
      setTimeout(onClose, 180);
    } else {
      el.style.transform = '';
    }
  };
  const stats = [
    ['Shifts worked', wt.s],
    ['Total spend', fmtUsd(wt.c)],
    ['Avg spend / shift', wt.s ? fmtUsd(wt.c / wt.s) : '$0'],
    ['Tasks closed', wt.t],
    ['Emails sent', wt.e],
    ['Dashboards maintained', emp.dashboards],
  ];
  // Portal to <body>: the page's reveal animations put CSS transforms on ancestor
  // sections, which re-anchor position:fixed — rendered in place, the modal ended
  // up off-screen at the top of the section instead of centered in the viewport.
  // The wrapper re-applies the .aie-landing theme classes because the --aie-*
  // variables live on the page wrapper, which the portal escapes.
  return createPortal(
    <div className={`aie-landing aio-modal-root ${dark ? 'dark' : 'light'}`}>
      <div className="aio-modal-backdrop" onClick={onClose} role="presentation">
      <div className="aio-modal" ref={sheetRef} role="dialog" aria-modal="true" aria-label={`${emp.name} profile`} onClick={(e) => e.stopPropagation()}>
        <div className="aio-modal__head" onTouchStart={headTouchStart} onTouchMove={headTouchMove} onTouchEnd={headTouchEnd}>
          <span className="aio-avatar aio-avatar--lg" aria-hidden="true">{emp.name[0]}</span>
          <span>
            <span className="aio-modal__name">{emp.name}</span>
            <span className="aio-modal__role">{emp.title} · hired {prettyDate(emp.hired)}{emp.reports_to && emp.reports_to !== 'Nitish' ? ` · reports to ${emp.reports_to}` : ''}</span>
            <span className="aio-modal__schedule">
              <StatusLine live={emp.live} nowMs={Date.now()} />
              {emp.live && emp.live.mode === 'on_demand'
                ? ' · started on demand, wakes on email and signals'
                : emp.live && emp.live.schedule
                  ? ` · ${emp.live.schedule} · typically ~${Math.round((emp.live.typical_shift_minutes || 360) / 60)}h`
                  : null}
            </span>
          </span>
          <button type="button" className="aio-modal__close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="aio-modal__stats">
          {stats.map(([l, v]) => <StatCard key={l} label={l} value={v} />)}
        </div>
        <Sparkline series={spark} className="aio-modal__spark" />
        <p className="aio-chart-note">Spend per day for the selected range. Email counts include internal coordination between employees.</p>
        <div className="aio-modal__cta">
          <p className="aio-modal__cta-txt"><strong>{emp.name} was hired in one click, like any CellCog employee.</strong> Yours would start their first shift today.</p>
          <button type="button" className="aie-btn aie-btn-primary" onClick={handleModalHire}>
            Hire an employee like this <ArrowRightOutlined />
          </button>
        </div>
      </div>
      </div>
    </div>,
    document.body,
  );
}

/* ---------- the BUILD half: super agents (v3) ---------- */

const monthLabel = (m) => {
  const [y, mo] = m.split('-').map(Number);
  return new Date(Date.UTC(y, mo - 1, 1)).toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
};

const BUILD_METRICS = [
  { key: 'c', label: 'Spend' },
  { key: 'ch', label: 'Chats' },
  { key: 'hc', label: 'Actions on our machines' },
];

// Continuous ET day list from era_start to the last recorded day.
function buildEraDays(sa) {
  const dailyKeys = Object.keys(sa.daily);
  if (!dailyKeys.length) return [];
  const last = dailyKeys.reduce((x, y) => (x > y ? x : y));
  const days = [];
  const cur = new Date(`${sa.era_start}T00:00:00Z`);
  const end = new Date(`${last}T00:00:00Z`);
  while (cur <= end) {
    days.push(cur.toISOString().slice(0, 10));
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return days;
}

// "+31% MoM (Jun)" chip from the monthly rollup — COMPLETE months only. The
// current month is partial; the hc series also starts mid-month (90-day audit
// retention), so its first month is partial too and must be skipped.
function momChip(sa, field) {
  const complete = sa.monthly.filter((m) => {
    if (m.month === sa.current_month) return false;
    if (field === 'hc_actions' && m.month <= sa.hc_window_start.slice(0, 7)) return false;
    return true;
  });
  if (complete.length < 2) return null;
  const last = complete[complete.length - 1];
  const prev = complete[complete.length - 2];
  if (!prev[field]) return null;
  const pct = Math.round(((last[field] - prev[field]) / prev[field]) * 100);
  return `${pct >= 0 ? '+' : ''}${pct}% MoM (${monthLabel(last.month)})`;
}

export function BuildDashboard() {
  const [data, setData] = useState(snapshot);
  const [metric, setMetric] = useState('c');
  useEffect(() => {
    fetch(`/assets/org-stats/org_stats.json?d=${new Date().toISOString().slice(0, 10)}`)
      // Guard on the DAILY super_agents shape: a stale cached JSON (pre-v3, or
      // v3's monthly-only draft) would otherwise blank this section at runtime.
      .then((r) => (r.ok ? r.json() : null))
      .then((fresh) => { if (fresh && fresh.super_agents && fresh.super_agents.daily) setData(filterOrgData(fresh)); })
      .catch(() => {});
  }, []);
  const sa = data.super_agents;
  const days = useMemo(() => (sa && sa.daily ? buildEraDays(sa) : []), [sa]);
  const [range, setRange] = useState([0, Math.max(days.length - 1, 0)]);
  useEffect(() => { setRange(([x, y]) => [Math.min(x, days.length - 1), y > days.length - 1 || y === 0 ? days.length - 1 : y]); }, [days.length]);
  if (!sa || !sa.daily || days.length < 2) return null;

  const [a, b] = range;
  const t = sa.totals;
  const metricDef = BUILD_METRICS.find((m) => m.key === metric);
  const fmtValue = metric === 'c' ? fmtUsd : fmt;

  // PER-METRIC HONESTY (Nitish): machine actions only exist since
  // hc_window_start (90-day audit retention) — clamp that series to where data
  // begins instead of drawing fake zeros for missing months.
  const hcIdx = Math.max(days.indexOf(sa.hc_window_start), 0);
  const effA = metric === 'hc' ? Math.max(a, hcIdx) : a;
  const series = [];
  for (let i = effA; i <= b; i += 1) {
    const d = sa.daily[days[i]];
    series.push(d ? d[metric] || 0 : 0);
  }
  const chartDays = days.slice(effA, b + 1);

  const setPreset = (n) => setRange(n === 0 ? [0, days.length - 1] : [Math.max(0, days.length - n), days.length - 1]);
  const isPreset = (n) => (n === 0 ? a === 0 && b === days.length - 1 : a === Math.max(0, days.length - n) && b === days.length - 1);

  return (
    <div>
      {/* ownership band: everything below belongs to the SUPER AGENTS —
          mirrors the AI-employees band in OrgLiveDashboard */}
      <div className="aio-band">
        <span className="aio-band__tag">Super Agents</span>
        <span className="aio-band__title">How we build the company</span>
      </div>

      <div className="aio-timebar">
        <span className="aio-range-label"><strong>{prettyDate(days[a])} – {prettyDate(days[b])}</strong> · times in ET</span>
        <span className="aio-presets">
          {[[30, 'Last 30 days'], [90, 'Last 90 days'], [0, 'Since Jan 1']].map(([n, l]) => (
            <button key={l} type="button" className={`aio-preset ${isPreset(n) ? 'is-active' : ''}`} onClick={() => setPreset(n)}>{l}</button>
          ))}
        </span>
      </div>
      <div className="aio-slider">
        <span className="aio-slider__track" />
        <span className="aio-slider__fill" style={{ left: `${(a / (days.length - 1)) * 100}%`, right: `${100 - (b / (days.length - 1)) * 100}%` }} />
        <input type="range" min="0" max={days.length - 1} value={a} aria-label="Start date" onChange={(e) => setRange([Math.min(+e.target.value, b), b])} />
        <input type="range" min="0" max={days.length - 1} value={b} aria-label="End date" onChange={(e) => setRange([a, Math.max(+e.target.value, a)])} />
      </div>
      <div className="aio-slider__dates"><span>{prettyDate(days[0])}</span><span>{prettyDate(days[days.length - 1])}</span></div>

      <div className="aio-totals" style={{ marginTop: 22 }}>
        <StatCard label="Chats run" value={fmt(t.chats)} sub={momChip(sa, 'chats') || 'since Jan 1, 2026'} />
        <StatCard label="Spend" value={fmtUsd(t.credits)} sub={momChip(sa, 'credits') || `≈ ${t.chats ? fmtUsd(t.credits / t.chats) : '$0'} per chat`} />
        <StatCard label="Actions on our machines" value={fmt(t.hc_actions)} sub={momChip(sa, 'hc_actions') || 'last 90 days · audited'} />
        <StatCard label="Workspace tool actions" value={fmt(t.workspace_actions)} sub="in chat sandboxes" />
        <StatCard label="Artifacts generated" value={fmt(t.artifacts)} sub="images · video · audio · documents" />
      </div>

      <div className="aio-chart-card">
        <div className="aio-chart-head">
          <span className="aio-chart-title">{metricDef.label} per day, super agents</span>
          <span className="aio-metric-toggle">
            {BUILD_METRICS.map((m) => (
              <button key={m.key} type="button" className={`aio-metric-btn ${metric === m.key ? 'is-active' : ''}`} onClick={() => { trackEvent('org_metric_toggle', { metric: m.key, surface: 'ai-organization-build' }); setMetric(m.key); }}>{m.label}</button>
            ))}
          </span>
        </div>
        {series.length > 1 ? (
          <ActivityChart days={chartDays} series={series} metricLabel={metricDef.label} fmtValue={fmtValue} />
        ) : (
          <p className="aio-chart-note">No machine-action records in this range — the per-command audit log keeps 90 days (since {prettyDate(sa.hc_window_start)}).</p>
        )}
        {metric === 'hc' && effA > a && series.length > 1 && (
          <p className="aio-chart-note">Machine actions are recorded since {prettyDate(sa.hc_window_start)} — the per-command audit log keeps 90 days, so this series starts there.</p>
        )}
      </div>
      <p className="aio-chart-note">
        Internal super-agent usage since Jan 1, 2026, from the company&rsquo;s own records (chat ledger, billing
        ledger, per-command machine audit log — the audit log keeps 90 days, so machine actions cover that window).
        Counts only — no content. Machine and workspace action counts are conservative undercounts. Refreshed daily.
      </p>
    </div>
  );
}

export default function OrgLiveDashboard({ dark = true, afterChart = null }) {
  const [data, setData] = useState(snapshot);
  const days = useMemo(() => buildDays(data), [data]);
  const [range, setRange] = useState([0, Math.max(days.length - 1, 0)]);
  const [metric, setMetric] = useState('c');
  const [openEmp, setOpenEmp] = useState(null);

  useEffect(() => {
    fetch(`/assets/org-stats/org_stats.json?d=${new Date().toISOString().slice(0, 10)}`)
      .then((r) => (r.ok ? r.json() : null))
      // Shape guards: a stale cached JSON would strip the presence indicators
      // (v5 guard: live block) or carry the OLD reporting lines from before
      // the v8 chart (guard: team_names — the server cron regenerates within
      // a day of deploy; until then the fresher baked snapshot wins).
      .then((fresh) => { if (fresh && fresh.employees && fresh.employees.length && fresh.employees[0].live && fresh.team_names) setData(filterOrgData(fresh)); })
      .catch(() => {});
  }, []);
  // Oct 6 2026 fix: the range sat at the BAKED snapshot's last day after the fresh
  // fetch added days, so every card stopped counting there (Nora, hired after the
  // bake, read 0). A range that ended on the old last day now follows the new one.
  const prevLenRef = React.useRef(days.length);
  useEffect(() => {
    const prevLen = prevLenRef.current;
    prevLenRef.current = days.length;
    const last = Math.max(days.length - 1, 0);
    setRange(([a, b]) => {
      const nb = b >= prevLen - 1 ? last : Math.min(b, last);
      return [Math.min(a, nb), nb];
    });
  }, [days.length]);

  const [a, b] = range;
  const perEmp = useMemo(
    () => data.employees.map((e) => {
      const wt = windowTotals(e, days, a, b);
      const spark = [];
      for (let i = a; i <= b; i += 1) spark.push(e.daily[days[i]] ? dayVal(e.daily[days[i]], 'c') : 0);
      return { emp: e, wt, spark };
    }),
    [data, days, a, b],
  );
  const org = perEmp.reduce((acc, { wt }) => ({
    s: acc.s + wt.s, c: acc.c + wt.c, t: acc.t + wt.t, e: acc.e + wt.e, d: acc.d + wt.d,
  }), { s: 0, c: 0, t: 0, e: 0, d: 0 });

  const chartSeries = useMemo(() => {
    const out = [];
    for (let i = a; i <= b; i += 1) {
      let v = 0;
      data.employees.forEach((e) => { const day = e.daily[days[i]]; if (day) v += dayVal(day, metric); });
      out.push(v);
    }
    return out;
  }, [data, days, a, b, metric]);

  const chartDays = days.slice(a, b + 1);
  const setPreset = (n) => setRange(n === 0 ? [0, days.length - 1] : [Math.max(0, days.length - n), days.length - 1]);
  const isPreset = (n) => (n === 0 ? a === 0 && b === days.length - 1 : a === Math.max(0, days.length - n) && b === days.length - 1);
  const metricLabel = METRICS.find((m) => m.key === metric).label;

  const { leads, levels, groups } = buildOrgTree(perEmp, data.team_names || snapshot.team_names);
  const opened = openEmp ? perEmp.find(({ emp }) => emp.name === openEmp) : null;

  if (!days.length) return null;
  return (
    <div>
      {/* the org graph LEADS (v4 landing reshuffle): founder -> leads -> level
          rows — the chart is the page's identity, so it renders before the
          totals and time controls. Shared OrgGraph markup (also the homepage
          hero); this instance opens the profile modal and re-windows with the
          time slider below. */}
      {/* interactivity hint (Aug 3 desktop pass): the cards' click affordance
          was hover-only — invisible until accidentally discovered. One plain
          line above the chart. */}
      <div className="aio-hint" role="note">
        <span className="aio-hint__dot" aria-hidden="true" />
        <span><strong>Every card below is live.</strong> Click any employee for their real shifts, spend, and activity — from our own records.</span>
      </div>
      <OrgGraph
        leads={leads}
        levels={levels}
        groups={groups}
        cardCta
        onCard={(emp, layout) => { trackEvent('org_chart_open', { employee: emp.name, surface: 'ai-organization', layout: layout || 'graph' }); setOpenEmp(emp.name); }}
        caption="Each team reports to its AI manager — AI employees managing AI employees. Click any card for the full profile."
      />

      {/* mid-page CTA strip slot (Aug 3): the page passes its strip in here so
          the first exit renders directly under the org-chart caption — the
          most-viewed spot on the page. */}
      {afterChart}

      {/* ownership band: the totals + activity chart below belong to the AI
          EMPLOYEES (the org above) — mirrored by the super-agents band in
          BuildDashboard */}
      <div className="aio-band">
        <span className="aio-band__tag">AI Employees</span>
        <span className="aio-band__title">How we scale the company</span>
      </div>

      {/* org totals */}
      <div className="aio-totals">
        <StatCard label="Shifts worked" value={org.s} />
        <StatCard label="Spend" value={fmtUsd(org.c)} sub={`≈ ${org.s ? fmtUsd(org.c / org.s) : '$0'} per shift`} />
        <StatCard label="Tasks closed" value={org.t} />
        <StatCard label="Emails sent" value={fmt(org.e)} sub="incl. internal coordination" />
        <StatCard label="Dashboard updates" value={org.d} />
      </div>

      {/* time control — sits WITH the activity chart it most visibly drives
          (it still re-windows the graph card minis above) */}
      <div className="aio-timebar">
        <span className="aio-range-label"><strong>{prettyDate(days[a])} – {prettyDate(days[b])}</strong> · times in ET</span>
        <span className="aio-presets">
          {[[7, 'Last 7 days'], [30, 'Last 30 days'], [0, 'All time']].map(([n, l]) => (
            <button key={l} type="button" className={`aio-preset ${isPreset(n) ? 'is-active' : ''}`} onClick={() => setPreset(n)}>{l}</button>
          ))}
        </span>
      </div>
      <div className="aio-slider">
        <span className="aio-slider__track" />
        <span className="aio-slider__fill" style={{ left: `${(a / (days.length - 1)) * 100}%`, right: `${100 - (b / (days.length - 1)) * 100}%` }} />
        <input type="range" min="0" max={days.length - 1} value={a} aria-label="Start date" onChange={(e) => setRange([Math.min(+e.target.value, b), b])} />
        <input type="range" min="0" max={days.length - 1} value={b} aria-label="End date" onChange={(e) => setRange([a, Math.max(+e.target.value, a)])} />
      </div>
      <div className="aio-slider__dates"><span>{prettyDate(days[0])}</span><span>{prettyDate(days[days.length - 1])}</span></div>

      {/* activity chart */}
      <div className="aio-chart-card">
        <div className="aio-chart-head">
          <span className="aio-chart-title">{metricLabel} per day, whole org</span>
          <span className="aio-metric-toggle">
            {METRICS.map((m) => (
              <button key={m.key} type="button" className={`aio-metric-btn ${metric === m.key ? 'is-active' : ''}`} onClick={() => { trackEvent('org_metric_toggle', { metric: m.key, surface: 'ai-organization' }); setMetric(m.key); }}>{m.label}</button>
            ))}
          </span>
        </div>
        <ActivityChart days={chartDays} series={chartSeries} metricLabel={metricLabel} fmtValue={metric === 'c' ? fmtUsd : fmt} />
      </div>
      <p className="aio-chart-note">Aggregated daily from the org&rsquo;s own records (shift ledger, task board, mailboxes). Counts only — no message content. Refreshed daily; last updated {prettyDate(data.generated_at.slice(0, 10))}.</p>

      {opened && <ProfileModal emp={opened.emp} wt={opened.wt} spark={opened.spark} dark={dark} onClose={() => setOpenEmp(null)} />}
    </div>
  );
}
